import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessage as APIChatMessage, ToolStep } from '../../api/data-contracts';
import { ChatSource } from '../../api/chatTypes';
import { JsonValue } from '../../types/common';
import {
  Sources,
  groupShowsTable,
  isOfficeSource,
  isImageSource,
  isXlsxGridSource,
} from './Sources';
import { MarkdownMessage } from './MarkdownMessage';
import { MessageContainer } from './MessageContainer';
import { CitationProvider } from './CitationContext';
import { MessageActions } from './MessageActions';
import { ToolStepsPanel } from './ToolStepsPanel';
import { TextSourceDrawer, type TextSourceTarget } from './TextSourceDrawer';
import { PdfPassageDrawer, type PdfPassageTarget } from './PdfPassageDrawer';
import { ImagePassageDrawer, type ImagePassageTarget } from './ImagePassageDrawer';
import { XlsxPassageDrawer, type XlsxPassageTarget } from './XlsxPassageDrawer';
import { renumberCitations } from './citationRenumber';
import { collapseNoLocatorCitations } from './collapseNoLocatorCitations';
import { isTextLikeSource } from '../../utils/documents/textSource';
import { viewOriginalDocument } from '../../utils/documents/documentHelpers';
import {
  locatorPassages,
  pickLocatorForStatement,
  pickQuoteIndexForStatement,
  sourceLocators,
} from './passageBinding';
import { log } from '../../utils/errors/logger';

/**
 * Strip inline [[N]] / [[N.x]] markers for document groups that render
 * header-only (no reference table — nothing for the chip to point at).
 * Markers for documents whose group shows the reference table are kept, so
 * the chip can scroll/flash to its rows. Delegates the header-only decision
 * to Sources.groupShowsTable so text and table can never disagree.
 */
function stripHeaderOnlyCitationMarkers(content: string, sources: ChatSource[]): string {
  const byDoc = new Map<string, ChatSource[]>();
  for (const s of sources) {
    if (s.citation_id === null || s.citation_id === undefined) {
      continue;
    }
    const doc = String(s.citation_id).split('.')[0];
    const group = byDoc.get(doc);
    if (group) {
      group.push(s);
    } else {
      byDoc.set(doc, [s]);
    }
  }
  // Remove "[[id]]" (and a single leading space) when its document group
  // renders header-only; unknown ids are removed too (nothing to point at).
  return content
    .replace(/\s?\[\[(\d+(?:\.\d+)?)\]\]/g, (full, id: string) => {
      const doc = String(id).split('.')[0];
      const group = byDoc.get(doc);
      return !group || !groupShowsTable(group) ? '' : full;
    })
    .replace(/[ \t]+([.,;:])/g, '$1')
    .trimEnd();
}

interface ChatMessage extends APIChatMessage {
  sources?: ChatSource[];
  /** Persisted message id (absent for the streaming placeholder). */
  id?: string;
  /** Server-side feedback value: 'up', 'down', or null/undefined. */
  feedback?: string | null;
  /**
   * MCP tool activity for this turn. Populated live from the SSE
   * accumulator (`toolStepAccumulator.ts`) while streaming; absent for
   * persisted messages until tool-step persistence lands (a later package),
   * in which case `ToolStepsPanel` simply renders nothing.
   */
  toolSteps?: ToolStep[];
}

interface ChatMessageProps {
  message: ChatMessage;
  isUser: boolean;
  /** The conversation this message belongs to — required to call the feedback API. */
  conversationId?: string;
}

const ChatMessageInner: React.FC<ChatMessageProps> = ({
  message,
  isUser,
  conversationId,
}) => {
  useTranslation(); // Keep for future use

  // Use exclusively the local sources of this message
  const rawSources: ChatSource[] = !isUser && message.sources ? [...message.sources] : [];

  // First collapse no-locator sources (image/URL/heading-less): merge their
  // per-chunk citations (1.1/1.2/1.3) into one whole-document reference ([[1]])
  // in BOTH the text and the source list, so the answer's chips match the
  // single collapsed reference row. Then renumber to make the cited ids
  // contiguous (the backend numbers ALL retrieved chunks but the answer cites
  // only a subset, leaving gaps). Both rewrites keep chips and rows in sync.
  let messageContent = message.content;
  let messageSources = rawSources;
  if (!isUser) {
    const collapsed = collapseNoLocatorCitations(message.content, rawSources);
    const renumbered = renumberCitations(collapsed.content, collapsed.sources);
    messageContent = renumbered.content;
    messageSources = renumbered.sources;

    // Single-row document groups render header-only (no table). For those, the
    // in-text [[N]]/[[N.x]] chip references a table that is not shown, so strip
    // it: a document cited from a single chunk needs no in-text marker — the
    // source header names it. Multi-row groups keep their chips.
    messageContent = stripHeaderOnlyCitationMarkers(messageContent, messageSources);
  }

  // Debug output for sources
  if (!isUser && messageSources.length > 0) {
    log.info(`Rendering message with ${messageSources.length} sources:`, {
      messagePreview: `${messageContent.substring(0, 30)  }...`,
      sources: messageSources as unknown as JsonValue
    });
  }

  const showSources = !isUser && messageSources.length > 0;

  // Shared text-source preview drawer, opened by BOTH the in-text citation links
  // (MarkdownMessage) and the source-row clicks (Sources). Resolving the source
  // by document_id here means a text/manual source previews in-app from either
  // place, instead of opening a raw-text browser tab.
  const [textSourceTarget, setTextSourceTarget] = React.useState<TextSourceTarget | null>(null);

  // Shared in-app PDF passage viewer, opened by BOTH the in-text citation links
  // and the source-row clicks — jumped to the cited page with the passage
  // highlighted.
  const [pdfPassageTarget, setPdfPassageTarget] = React.useState<PdfPassageTarget | null>(null);

  // Shared in-app image viewer: cited OCR regions are overlaid on the image.
  const [imagePassageTarget, setImagePassageTarget] = React.useState<ImagePassageTarget | null>(null);

  // Shared in-app spreadsheet viewer: cited sheet + cell range are activated.
  const [xlsxPassageTarget, setXlsxPassageTarget] = React.useState<XlsxPassageTarget | null>(null);

  const openTextSourceOrFallback = React.useCallback(
    (documentId: string, statement?: string): void => {
      const source = messageSources.find((s) => s.document_id === documentId);
      // NotebookLM binding: when the click came from an inline chip, pick the
      // passage that supports the chip's statement instead of always the
      // document's first passage.
      const locator = source ? pickLocatorForStatement(source, statement) : null;
      if (source && isXlsxGridSource(source) && source.document_id) {
        setXlsxPassageTarget({
          documentId,
          title: source.title,
          sheetName: locator?.sheet_name ?? locator?.section_path ?? null,
          a1Range: locator?.a1_range ?? null,
        });
        return;
      }
      if (source && isTextLikeSource(source)) {
        setTextSourceTarget({
          documentId,
          title: source.title,
          excerpt: locator?.excerpt ?? source.excerpt,
        });
        return;
      }
      const isPdf =
        (source?.file_type ?? '').toLowerCase() === 'pdf' ||
        (source?.title ?? '').toLowerCase().endsWith('.pdf');
      const isOffice = source ? isOfficeSource(source) : false;
      if (source && (isPdf || isOffice)) {
        const label = source.citation_id !== null && source.citation_id !== undefined
          ? String(source.citation_id).split('.')[0]
          : null;
        // Every supporting window of the document is marked; the passage
        // bound to the clicked statement is the primary (jump) target, the
        // rest are drawn as secondary frames.
        const others = sourceLocators(source).filter((l) => l !== locator);
        const passages = [
          ...locatorPassages(source, locator, label, true, pickQuoteIndexForStatement(locator, statement)),
          ...others.flatMap((l) => locatorPassages(source, l, label, false)),
        ];
        const [first] = passages;
        setPdfPassageTarget({
          documentId,
          title: source.title,
          pages: first?.pages ?? null,
          quote: first?.quote ?? null,
          quotePrefix: first?.quotePrefix ?? null,
          quoteSuffix: first?.quoteSuffix ?? null,
          regions: first?.regions ?? null,
          src: isOffice ? 'preview' : 'original',
          label,
          passages: passages.length > 1 ? passages : null,
        });
        return;
      }
      if (source && isImageSource(source) && source.document_id) {
        // A chart is cited through several chunks: the title is one, the
        // values another, each with its own boxes. Passing only the clicked
        // one left the numbers unmarked, so every cited window is handed
        // over and the clicked one leads.
        const imageLabel = source.citation_id !== null && source.citation_id !== undefined
          ? String(source.citation_id).split('.')[0]
          : null;
        const imagePassages = sourceLocators(source).map((l) => ({
          regions: l.regions ?? null,
          label: imageLabel,
          primary: l === locator,
        }));
        setImagePassageTarget({
          documentId,
          title: source.title,
          regions: locator?.regions ?? null,
          label: imageLabel,
          passages: imagePassages.length > 0 ? imagePassages : null,
        });
        return;
      }
      // Neither text nor viewer-capable: keep the existing open-original behavior.
      void viewOriginalDocument(documentId);
    },
    [messageSources],
  );

  return (
    <CitationProvider onOpenDocument={openTextSourceOrFallback}>
      <MessageContainer isUser={isUser}>
        {!isUser && <ToolStepsPanel steps={message.toolSteps} />}

        <MarkdownMessage
          content={messageContent}
          isUser={isUser}
          sources={messageSources}
          onOpenDocument={openTextSourceOrFallback}
        />

        {/* Action row: copy + thumbs-up/down — assistant messages only */}
        {!isUser && (
          <MessageActions
            content={messageContent}
            sources={messageSources}
            messageId={message.id}
            conversationId={conversationId}
            feedback={message.feedback}
          />
        )}
      </MessageContainer>

      {/* Reference sources. A group whose table would have a single row renders
          header-only (handled inside Sources via groupShowsTable). */}
      {showSources && (
        <Box sx={{ pl: 2, pt: 1, pb: 3, width: '100%' }}>
          <Sources
            sources={messageSources}
            onOpenTextSource={(t) => setTextSourceTarget(t)}
            onOpenPdfPassage={(t) => setPdfPassageTarget(t)}
            onOpenImagePassage={(t) => setImagePassageTarget(t)}
            onOpenXlsxPassage={(t) => setXlsxPassageTarget(t)}
          />
        </Box>
      )}

      <TextSourceDrawer target={textSourceTarget} onClose={() => setTextSourceTarget(null)} />
      <PdfPassageDrawer target={pdfPassageTarget} onClose={() => setPdfPassageTarget(null)} />
      <ImagePassageDrawer target={imagePassageTarget} onClose={() => setImagePassageTarget(null)} />
      <XlsxPassageDrawer target={xlsxPassageTarget} onClose={() => setXlsxPassageTarget(null)} />
    </CitationProvider>
  );
};

/**
 * Memoized so typing in the chat input (which re-renders the parent on every
 * keystroke) does not re-render every prior message's markdown + source table.
 * Props are a stable per-message object plus primitives, so the default shallow
 * comparison is correct: a message only re-renders when its own data changes.
 */
export const ChatMessageComponent = React.memo(ChatMessageInner);
